import { useState } from 'react';

import type { EntryMode, Match, Team, TeamId } from '@/core/schemas';
import type { Side } from '@/core/engine/x01/types';
import { sideLabel } from '@/features/matches/matchPresentation';
import { Button } from '@/shared/ui/Button';
import { cn } from '@/shared/utils/cn';

interface CounterStartFormProps {
  match: Match;
  lookup: (teamId: TeamId) => Team | undefined;
  startScore: number;
  doubleOut: boolean;
  isSubmitting: boolean;
  onStart: (entryMode: EntryMode, startingSide: Side) => void;
}

/**
 * Opens the counter at the board: choose how throws are entered and which team
 * throws first. Both are decided once, at the start, as requested.
 */
export function CounterStartForm({
  match,
  lookup,
  startScore,
  doubleOut,
  isSubmitting,
  onStart,
}: CounterStartFormProps) {
  const [entryMode, setEntryMode] = useState<EntryMode>('sum');
  const [startingSide, setStartingSide] = useState<Side>('home');

  return (
    <section className="border-line bg-surface flex flex-col gap-5 rounded-xl border p-5">
      <div>
        <h2 className="text-lg font-semibold">Counter starten</h2>
        <p className="text-fg-muted mt-1 text-sm">
          {startScore} Punkte, {doubleOut ? 'Double-Out' : 'Single-Out'}. Ein Gerät zählt für beide
          Teams.
        </p>
      </div>

      <Choice
        label="Eingabe"
        value={entryMode}
        options={[
          { value: 'sum', label: 'Aufnahme-Summe' },
          { value: 'darts', label: 'Einzelne Darts' },
        ]}
        onChange={setEntryMode}
      />

      <Choice
        label="Anwurf"
        value={startingSide}
        options={[
          { value: 'home', label: sideLabel(match.home, lookup) },
          { value: 'away', label: sideLabel(match.away, lookup) },
        ]}
        onChange={setStartingSide}
      />

      <Button
        onClick={() => onStart(entryMode, startingSide)}
        isLoading={isSubmitting}
        className="sm:self-start"
      >
        Spiel starten
      </Button>
    </section>
  );
}

interface ChoiceProps<T extends string> {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}

function Choice<T extends string>({ label, value, options, onChange }: ChoiceProps<T>) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-fg-subtle text-xs tracking-wide uppercase">{label}</span>
      <div className="grid grid-cols-2 gap-2">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={cn(
              'touch-target truncate rounded-lg border px-4 py-3 text-sm font-medium',
              value === option.value
                ? 'bg-brand text-brand-fg border-transparent'
                : 'bg-surface-raised border-line',
            )}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}
